import type {
  ChangePasswordInput,
  ForgotPasswordInput,
  RegenerateRecoveryCodesInput,
  ResetPasswordInput,
  TwoFactorChallengeInput,
  TwoFactorDisableInput,
  TwoFactorSetupResponse,
  TwoFactorStatusDTO,
  TwoFactorVerifyInput,
  TwoFactorVerifyResponse,
} from '@bruno/shared';
import { api, setAccessToken } from './client';
import type { AuthResponse, LoginResponse } from '@bruno/shared';

export function changePassword(body: ChangePasswordInput): Promise<void> {
  return api<void>('/v1/account/password', { method: 'POST', body });
}

export function getTwoFactorStatus(): Promise<TwoFactorStatusDTO> {
  return api<TwoFactorStatusDTO>('/v1/account/2fa/status');
}

export function setupTwoFactor(): Promise<TwoFactorSetupResponse> {
  return api<TwoFactorSetupResponse>('/v1/account/2fa/setup', { method: 'POST' });
}

export function verifyTwoFactor(body: TwoFactorVerifyInput): Promise<TwoFactorVerifyResponse> {
  return api<TwoFactorVerifyResponse>('/v1/account/2fa/verify', { method: 'POST', body });
}

export function disableTwoFactor(body: TwoFactorDisableInput): Promise<void> {
  return api<void>('/v1/account/2fa/disable', { method: 'POST', body });
}

export function regenerateRecoveryCodes(
  body: RegenerateRecoveryCodesInput,
): Promise<TwoFactorVerifyResponse> {
  return api<TwoFactorVerifyResponse>('/v1/account/2fa/recovery-codes', {
    method: 'POST',
    body,
  });
}

export function forgotPassword(body: ForgotPasswordInput): Promise<void> {
  return api<void>('/v1/auth/forgot-password', {
    method: 'POST',
    body,
    retryOn401: false,
  });
}

export function resetPassword(body: ResetPasswordInput): Promise<void> {
  return api<void>('/v1/auth/reset-password', {
    method: 'POST',
    body,
    retryOn401: false,
  });
}

/**
 * Completes a 2FA-gated login by exchanging the short-lived challenge
 * ticket for a session. Mirrors the regular login path: stash the
 * access token in memory and return the AuthResponse.
 */
export async function twoFactorLogin(body: TwoFactorChallengeInput): Promise<AuthResponse> {
  const res = await api<LoginResponse>('/v1/auth/2fa/login', {
    method: 'POST',
    body,
    retryOn401: false,
  });
  if ('requires2fa' in res) {
    // Server should never re-issue a challenge here; guard for safety.
    throw new Error('Unexpected 2FA challenge from /2fa/login');
  }
  setAccessToken(res.accessToken);
  return res;
}
